/**
 * Supplier claim API: POST endpoints use direct JSON fetch + bearer token (same pattern as
 * migration-tool staged-supplier calls in migrationTool.api.ts). Response/request types come from
 * onboarding-workflows OpenAPI (supplierClaim.openapi.types).
 * Session keys for claim staging / prompt dismissal are client-only (not in spec).
 */

import { protectedResources } from '@/authConfig';
import acquireAccessToken from '@/http/Helpers/acquireAccessToken';

import type {
  CheckStagedRegistrationRequest,
  CheckStagedSupplierRegistrationResult,
  CompleteSupplierClaimResult,
  RedeemSupplierClaimResult
} from '../types/supplierClaim.openapi.types';

// --- Session (client-only) ---

const STAGING_ID_KEY = 'b1link_supplier_claim_staging_id';
/** JSON snapshot from redeem response — used to re-apply prefill after navigation or failed save. */
const STAGING_DETAIL_JSON_KEY = 'b1link_supplier_claim_staging_detail_json';
/** When true, registration should retry `applyStagedSupplierPrefill` (e.g. no companyId yet, or save failed). */
const PREFILL_PENDING_KEY = 'b1link_supplier_claim_prefill_pending';
/** After a successful SaveEntireCompany prefill for this company id; cleared when staging session is cleared. */
const PREFILL_COMPLETED_COMPANY_KEY = 'b1link_supplier_claim_prefill_completed_company';
/** After shareholders + trade refs POSTed from staging for this company; cleared with staging session. */
const SHAREHOLDERS_TRADEREF_PERSISTED_COMPANY_KEY =
  'b1link_claim_shareholders_traderef_persisted_company';
const PROMPT_DISMISSED_KEY = 'b1link_supplier_claim_prompt_dismissed';

/** Removes staging id, detail JSON, prefill pending, and completed-company keys (not the claim prompt flag). */
function clearStagingAndPrefillSessionKeys(): void {
  try {
    sessionStorage.removeItem(STAGING_ID_KEY);
    sessionStorage.removeItem(STAGING_DETAIL_JSON_KEY);
    sessionStorage.removeItem(PREFILL_PENDING_KEY);
    sessionStorage.removeItem(PREFILL_COMPLETED_COMPANY_KEY);
    sessionStorage.removeItem(SHAREHOLDERS_TRADEREF_PERSISTED_COMPANY_KEY);
  } catch {
    /* ignore */
  }
}

export function getShareholdersTradeRefPersistedCompanyId(): string | null {
  try {
    return sessionStorage.getItem(SHAREHOLDERS_TRADEREF_PERSISTED_COMPANY_KEY);
  } catch {
    return null;
  }
}

export function markShareholdersTradeRefPersistedForCompany(companyId: number): void {
  try {
    sessionStorage.setItem(
      SHAREHOLDERS_TRADEREF_PERSISTED_COMPANY_KEY,
      String(companyId)
    );
  } catch {
    /* ignore */
  }
}

/**
 * Clears all client-side supplier-claim session keys, including the intro prompt dismissal flag.
 * Call on logout so the next account does not inherit another user's staging / prefill state.
 */
export function clearSupplierClaimClientSession(): void {
  clearStagingAndPrefillSessionKeys();
  try {
    sessionStorage.removeItem(PROMPT_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredSupplierClaimStagingId(): string | null {
  try {
    return sessionStorage.getItem(STAGING_ID_KEY);
  } catch {
    return null;
  }
}

export function setStoredSupplierClaimStagingId(stagingSupplierId: string | null) {
  try {
    if (stagingSupplierId) {
      const prev = getStoredSupplierClaimStagingId();
      if (prev && prev.toLowerCase() !== stagingSupplierId.trim().toLowerCase()) {
        sessionStorage.removeItem(PREFILL_COMPLETED_COMPANY_KEY);
      }
      sessionStorage.setItem(STAGING_ID_KEY, stagingSupplierId);
    } else {
      clearStagingAndPrefillSessionKeys();
    }
  } catch {
    /* ignore */
  }
}

export function getStoredSupplierClaimStagingDetailJson(): string | null {
  try {
    return sessionStorage.getItem(STAGING_DETAIL_JSON_KEY);
  } catch {
    return null;
  }
}

export function setStoredSupplierClaimStagingDetailJson(json: string | null) {
  try {
    if (json) sessionStorage.setItem(STAGING_DETAIL_JSON_KEY, json);
    else sessionStorage.removeItem(STAGING_DETAIL_JSON_KEY);
  } catch {
    /* ignore */
  }
}

export function getSupplierClaimPrefillPending(): boolean {
  try {
    return sessionStorage.getItem(PREFILL_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

/** Fired when prefill should retry (session updated). RegistrationBaseLayout listens to re-run migration prefill. */
export const SUPPLIER_CLAIM_PREFILL_PENDING_EVENT =
  'b1link-supplier-claim-prefill-pending';

export function setSupplierClaimPrefillPending(pending: boolean) {
  try {
    if (pending) sessionStorage.setItem(PREFILL_PENDING_KEY, '1');
    else sessionStorage.removeItem(PREFILL_PENDING_KEY);
    if (pending && typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new CustomEvent(SUPPLIER_CLAIM_PREFILL_PENDING_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function getPrefillCompletedCompanyId(): string | null {
  try {
    return sessionStorage.getItem(PREFILL_COMPLETED_COMPANY_KEY);
  } catch {
    return null;
  }
}

export function setPrefillCompletedCompanyId(companyId: number | null) {
  try {
    if (companyId != null && companyId > 0) {
      sessionStorage.setItem(PREFILL_COMPLETED_COMPANY_KEY, String(companyId));
    } else {
      sessionStorage.removeItem(PREFILL_COMPLETED_COMPANY_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Parses session JSON (detail DTO or full redeem wrapper) and returns the root object for
 * `applyStagedSupplierPrefill` plus the staging row id for matching `b1link_supplier_claim_staging_id`.
 */
/** Staging row id from redeem/detail DTO (camelCase or PascalCase serializers). */
export function getStagingSupplierIdFromDetail(detail: unknown): string | null {
  if (detail == null || typeof detail !== 'object') return null;

  const raw =
    (detail as Record<string, unknown>).id ?? (detail as Record<string, unknown>).Id;

  return typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : null;
}

/**
 * Normalizes check-staged-registration JSON (camelCase or PascalCase) for all call sites.
 */
export function readCheckStagedSupplierRegistrationResult(res: unknown): {
  inStaging: boolean;
  isClaimCompleted: boolean;
  stagingSupplierId: string;
  redeemAlreadyInProgressForCurrentUser: boolean;
  claimLinkedToAnotherAccount: boolean;
  emailMatchesStagedPrimaryContact: boolean;
  stagedPrimaryContactEmailMasked: string | null;
} {
  if (res == null || typeof res !== 'object') {
    return {
      inStaging: false,
      isClaimCompleted: false,
      stagingSupplierId: '',
      redeemAlreadyInProgressForCurrentUser: false,
      claimLinkedToAnotherAccount: false,
      emailMatchesStagedPrimaryContact: false,
      stagedPrimaryContactEmailMasked: null
    };
  }
  const o = res as Record<string, unknown>;
  const sidRaw = o.stagingSupplierId ?? o.StagingSupplierId;
  const masked = o.stagedPrimaryContactEmailMasked ?? o.StagedPrimaryContactEmailMasked;
  return {
    inStaging: o.inStaging === true || o.InStaging === true,
    isClaimCompleted: o.isClaimCompleted === true || o.IsClaimCompleted === true,
    stagingSupplierId:
      typeof sidRaw === 'string' || typeof sidRaw === 'number'
        ? String(sidRaw).trim()
        : '',
    redeemAlreadyInProgressForCurrentUser:
      o.redeemAlreadyInProgressForCurrentUser === true ||
      o.RedeemAlreadyInProgressForCurrentUser === true,
    claimLinkedToAnotherAccount:
      o.claimLinkedToAnotherAccount === true || o.ClaimLinkedToAnotherAccount === true,
    emailMatchesStagedPrimaryContact:
      o.emailMatchesStagedPrimaryContact === true ||
      o.EmailMatchesStagedPrimaryContact === true,
    stagedPrimaryContactEmailMasked:
      typeof masked === 'string' && masked.length ? masked : null
  };
}

export function parseStagingDetailJsonForPrefill(
  detailJson: string
): { detailRoot: unknown; stagingRowId: string } | null {
  let root: Record<string, unknown>;
  try {
    root = JSON.parse(detailJson) as Record<string, unknown>;
  } catch {
    return null;
  }

  const inner = (root.stagedSupplierDetail ??
    root.StagedSupplierDetail ??
    root) as Record<string, unknown>;
  const raw = inner.id ?? inner.Id;
  const stagingRowId =
    typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : '';
  if (!stagingRowId) {
    return null;
  }
  return { detailRoot: root, stagingRowId };
}

export function isSupplierClaimPromptDismissed(): boolean {
  try {
    return sessionStorage.getItem(PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSupplierClaimPromptDismissed() {
  try {
    sessionStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

// --- HTTP (JSON POST; aligned with migrationTool.api manual fetch + auth) ---

// Paths are relative to onboardingWebApi.endpoint (env already includes trailing /api).
// Do not prefix /api — same as migrationTool.api getUploadUrl (/migration-tool/...).
const REDEEM_PATH = '/migration-tool/supplier-claims/redeem' as const;
const COMPLETE_PATH = '/migration-tool/supplier-claims/complete' as const;
const CHECK_STAGED_REGISTRATION_PATH =
  '/migration-tool/supplier-claims/check-staged-registration' as const;

function stagedSupplierDetailForRedeemerPath(stagingSupplierId: string): string {
  return `/migration-tool/supplier-claims/staged-supplier-detail/${encodeURIComponent(
    stagingSupplierId.trim()
  )}`;
}

/**
 * Loads staged import detail for the current user when they have already redeemed the claim
 * (session lost or new device). Returns null on 403/404.
 */
export async function fetchStagedSupplierDetailForRedeemer(
  stagingSupplierId: string
): Promise<Record<string, unknown> | null> {
  const id = stagingSupplierId.trim();
  if (!id) return null;

  const base = protectedResources.onboardingWebApi.endpoint.trim().replace(/\/$/, '');
  const token = await acquireAccessToken(protectedResources.onboardingWebApi.scopes);
  const res = await fetch(`${base}${stagedSupplierDetailForRedeemerPath(id)}`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  if (res.status === 403 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const text = await res.text();
    const err = new Error(text || res.statusText);
    throw err;
  }
  return res.json() as Promise<Record<string, unknown>>;
}

async function onboardingPostJson<T>(path: string, body: unknown): Promise<T> {
  const base = protectedResources.onboardingWebApi.endpoint.trim().replace(/\/$/, '');
  const token = await acquireAccessToken(protectedResources.onboardingWebApi.scopes);
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function redeemSupplierClaim(
  claimCode: string
): Promise<RedeemSupplierClaimResult> {
  const raw = await onboardingPostJson<Record<string, unknown>>(REDEEM_PATH, {
    claimCode
  });
  const o = raw ?? {};
  const success = o.success === true || o.Success === true;
  const errorMessage = (o.errorMessage ?? o.ErrorMessage) as string | null | undefined;
  const detail = (o.stagedSupplierDetail ?? o.StagedSupplierDetail) as
    | RedeemSupplierClaimResult['stagedSupplierDetail']
    | undefined;
  return {
    success,
    stagedSupplierDetail: detail,
    errorMessage: errorMessage ?? null
  };
}

export async function completeSupplierClaim(
  stagingSupplierId: string
): Promise<CompleteSupplierClaimResult> {
  const raw = await onboardingPostJson<Record<string, unknown>>(COMPLETE_PATH, {
    stagingSupplierId
  });
  const o = raw ?? {};
  const success = o.success === true || o.Success === true;
  const errorMessage = (o.errorMessage ?? o.ErrorMessage) as string | null | undefined;
  return { success, errorMessage: errorMessage ?? null };
}

export function checkStagedSupplierRegistration(
  body: CheckStagedRegistrationRequest
): Promise<CheckStagedSupplierRegistrationResult> {
  return onboardingPostJson<CheckStagedSupplierRegistrationResult>(
    CHECK_STAGED_REGISTRATION_PATH,
    body
  );
}

/**
 * When sessionStorage lost the staging id (e.g. refresh, early clear), recover it from
 * check-staged-registration so the final registration step can persist shareholders/trade refs and complete.
 */
export async function resolveStagingSupplierIdForRegistrationFinalize(params: {
  registrationNumber: string | undefined;
  accountEmail: string | undefined;
}): Promise<string | null> {
  const existing = getStoredSupplierClaimStagingId();
  if (existing?.trim()) return existing.trim();

  const reg = params.registrationNumber?.trim() ?? '';
  const email = params.accountEmail?.trim() ?? '';
  if (!reg || !email) return null;

  try {
    const raw = await checkStagedSupplierRegistration({
      registrationNumber: reg,
      accountEmail: email
    });
    const check = readCheckStagedSupplierRegistrationResult(raw);
    if (
      !check.inStaging ||
      !check.stagingSupplierId ||
      check.claimLinkedToAnotherAccount
    ) {
      return null;
    }
    setStoredSupplierClaimStagingId(check.stagingSupplierId);
    return check.stagingSupplierId;
  } catch {
    return null;
  }
}
